import { useState } from "react";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, Users, Hammer, ShoppingBag, IndianRupee, Factory, TrendingUp, Receipt, Loader2, DatabaseBackup } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const REPORTS = [
  { type: "customers", label: "Customer Report", desc: "All customers with order counts & pending amounts", icon: Users },
  { type: "karigars", label: "Karigar Report", desc: "Work totals, paid & pending per karigar", icon: Hammer },
  { type: "pending-orders", label: "Pending Orders", desc: "All orders not yet delivered", icon: ShoppingBag },
  { type: "pending-payments", label: "Pending Payments", desc: "Orders with remaining customer payments", icon: IndianRupee },
  { type: "production", label: "Shop Production", desc: "Design-wise production progress & costing", icon: Factory },
  { type: "profit", label: "Profit Report", desc: "Profit on all delivered products", icon: TrendingUp },
  { type: "expenses", label: "Expense Report", desc: "All expenses by date & category", icon: Receipt },
];

export default function Reports() {
  const [loading, setLoading] = useState("");

  const download = async (type, format) => {
    const key = `${type}-${format}`;
    setLoading(key);
    try {
      const res = await api.get(`/reports/${type}`, { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}-report.${format === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch {
      toast.error("Failed to generate report");
    } finally {
      setLoading("");
    }
  };

  const backup = async () => {
    setLoading("backup");
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `anamta-noor-backup-${new Date().toLocaleDateString("en-CA")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Full backup downloaded");
    } catch {
      toast.error("Failed to create backup");
    } finally {
      setLoading("");
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">Export business reports as PDF or Excel</p>
        </div>
        <Button
          data-testid="backup-export-button"
          className="rounded-full active:scale-95 transition-transform"
          disabled={loading === "backup"}
          onClick={backup}
        >
          {loading === "backup" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <DatabaseBackup className="h-4 w-4 mr-1.5" />}
          Full Backup (Excel)
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {REPORTS.map(({ type, label, desc, icon: Icon }) => (
          <div key={type} data-testid={`report-card-${type}`} className="rounded-xl border bg-card p-6 flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <p className="font-heading font-semibold">{label}</p>
                <p className="text-sm text-muted-foreground">{desc}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-auto">
              <Button
                variant="outline"
                size="sm"
                data-testid={`download-${type}-pdf`}
                className="rounded-lg flex-1"
                disabled={loading === `${type}-pdf`}
                onClick={() => download(type, "pdf")}
              >
                {loading === `${type}-pdf` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileText className="h-3.5 w-3.5 mr-1.5" /> PDF</>}
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid={`download-${type}-excel`}
                className="rounded-lg flex-1"
                disabled={loading === `${type}-excel`}
                onClick={() => download(type, "excel")}
              >
                {loading === `${type}-excel` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Excel</>}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
